import { Grid } from '@material-ui/core'
import TableCell from '@material-ui/core/TableCell'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { PolicyTimeslot_DaysEnum, PolicyRule_VehicleTypesEnum, PolicyTimeslot } from 'src/api/gen/api'
import { IPolicyRuleFleetBudget } from 'src/api/types/policy'
import { IBaseTableProps } from 'src/commons/policies/rules'
import { TRulesValidation } from 'src/commons/policies/RulesValidation'
import { isEndTimeBeforeStartTime } from 'src/commons/policies/utils'
import { SimpleTable } from 'src/components/Table'
import SETTINGS from 'src/settings'

import NumericInput from '../../../../components/Input/NumericInput'
import { FieldTitle } from '../../commons'
import { GeographiesField, RuleNameField, VehicleTypesField } from '../fields'

import TimeSlotForm from './TimeSlot'

type Row = [
  TRulesValidation,
  string, // name
  string, // ruleId
  PolicyRule_VehicleTypesEnum[], // vehicleTypes
  string[], // geographies
  number, // maximum
  number //amount
]

function FleetBudgetRule(props: IBaseTableProps<IPolicyRuleFleetBudget>) {
  const { t } = useTranslation()

  const HEADERS = [
    t('rule.name'),
    t('rule.count.vehicle_types'),
    t('rule.geographies'),
    t('rule.fleet_budget.maximum'),
    t('headers.amount'),
  ]

  const handleChangeRule =
    (ruleId: string, key: keyof IPolicyRuleFleetBudget) =>
    (val: string | string[] | number | PolicyRule_VehicleTypesEnum[] | null): void => {
      const newRules: IPolicyRuleFleetBudget[] = props.rules.map((rule) => {
        if (rule.rule_id !== ruleId) {
          return rule
        }
        return { ...rule, [key]: val }
      })
      props.handleChange(newRules)
    }

  function handleChangeTimeslot(timeslot: PolicyTimeslot, index: number, key: keyof PolicyTimeslot) {
    return (val: string | PolicyTimeslot_DaysEnum[] | null): void => {
      const timeslotsToUpdate: PolicyTimeslot[] = props.rules[0].timeslots
      timeslotsToUpdate[index] = { ...timeslot, [key]: val }
      const newRules: IPolicyRuleFleetBudget[] = [{ ...props.rules[0], ['timeslots']: timeslotsToUpdate }]
      props.handleChange(newRules)
    }
  }

  const items = props.rules.map(
    (rule: IPolicyRuleFleetBudget, idx): Row => [
      props.validation[idx],
      rule.name,
      rule.rule_id,
      rule.vehicle_types,
      rule.geographies,
      rule.maximum,
      rule.rate_amount,
    ]
  )

  return (
    <>
      <SimpleTable
        headerRow={HEADERS.map((title: string) => (
          <TableCell key={`key-${title}`}>
            <FieldTitle title={title} />
          </TableCell>
        ))}
        items={items}
        rowRenderer={([ruleValidation, name, ruleId, vehicleTypes, geographies, maximum, rate_amount]: Row) => (
          <>
            <TableCell style={{ height: '100px', verticalAlign: 'bottom' }} align="justify">
              <RuleNameField
                value={name}
                handleChange={handleChangeRule(ruleId, 'name')}
                disabled={props.disabled}
                error={!ruleValidation.isFieldValid('name')}
                helperText={ruleValidation.getFieldErrorMsg('name')}
              />
            </TableCell>
            <TableCell style={{ height: '100px', verticalAlign: 'bottom', maxWidth: '280px' }} align="justify">
              <VehicleTypesField
                value={vehicleTypes}
                handleChange={handleChangeRule(ruleId, 'vehicle_types')}
                disabled={props.disabled}
                error={!ruleValidation.isFieldValid('vehicle_types')}
                helperText={ruleValidation.getFieldErrorMsg('vehicle_types')}
              />
            </TableCell>

            <TableCell style={{ verticalAlign: 'bottom', height: '100px', maxWidth: '280px' }} align="justify">
              <GeographiesField
                id="fleet_budget_geographies"
                value={geographies}
                allGeographies={props.allGeographies}
                handleChange={handleChangeRule(ruleId, 'geographies')}
                disabled={props.disabled}
                error={!ruleValidation.isFieldValid('geographies')}
                helperText={ruleValidation.getFieldErrorMsg('geographies')}
              />
            </TableCell>
            <TableCell style={{ height: '100px', verticalAlign: 'bottom', maxWidth: '200px' }} align="justify">
              <NumericInput
                id="fleet_budget_maximum_fleet_deployment"
                value={maximum}
                onChange={handleChangeRule(ruleId, 'maximum')}
                disabled={props.disabled}
              />
            </TableCell>
            <TableCell style={{ height: '100px', verticalAlign: 'bottom', maxWidth: '200px' }} align="justify">
              <NumericInput
                id="fleet_budget_amount"
                value={rate_amount}
                onChange={handleChangeRule(ruleId, 'rate_amount')}
                disabled={props.disabled}
                currency={props.currencySymbol}
              />
            </TableCell>
          </>
        )}
      />
      {SETTINGS.app.enable_timeslots && (
        <Grid container spacing={3}>
          {props.rules[0].timeslots.map((timeslot, index) => {
            return (
              <Grid item xs={3} key={index}>
                <Grid container direction="column" spacing={1} style={{ marginTop: '13px' }}>
                  <Grid item component="h3">
                    {t('rule.time_slot_count', { index: index + 1 })}
                  </Grid>
                  <Grid item>
                    <TimeSlotForm
                      timeslot={timeslot}
                      handleChangeDays={handleChangeTimeslot(timeslot, index, 'days')}
                      handleChangeStartTime={handleChangeTimeslot(timeslot, index, 'start_time')}
                      handleChangeEndTime={handleChangeTimeslot(timeslot, index, 'end_time')}
                      handleRemoveTimeSlot={() => props.handleRemoveTimeSlot(index)}
                      disabled={props.isPublished && props.disabled}
                      error={isEndTimeBeforeStartTime(timeslot)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )
          })}
        </Grid>
      )}
    </>
  )
}

export default FleetBudgetRule
